import { SortDirection } from 'Components/Table/SortableTableHeader/SortableTableHeader';
import { toTitleCase } from 'Helpers/StringUtils';
import { compareUsersBySubjectForSorting } from 'Helpers/UserUtils';
import { FileToBeUploaded, FileUpdates, UploadedFile } from 'Models/Files';
import { GroupOptionType, OptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import { OperationalControl } from './OperationalControls';
import { thirdPartyNameComparator } from './TPRM';
import { DisplayableTag } from './Tags';

// TODO: move to a central location, since this is also used by Exceptions.
export enum IssuesExceptionsModule {
    CONTROLS = 'Control Automation',
    TPRM = 'Third-Party Risk Management',
}

/**
 * Create and update requests write this to the database. This is not sent in responses. Instead, it is converted to a DTO, since the front-end needs to know:
 * 1. Whether the issue belongs to the Operational Controls module or the TPRM module.
 * 2. Additional details about the controls that an issue is mapped to, if it belongs in the Operational Controls module.
 */
export interface Issue {
    id: string;
    status: IssueStatus;
    owner_subject: string;
    title: string;
    due_date: string;
    description: string;
    priority: IssuePriority;
    source: string;
    created_timestamp: string;
    remediation_plan: string;
    risk_assessment: string;
    created_by: string;
    last_updated_by: string;
    last_updated_timestamp: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    files: UploadedFile[];
    tags: string[];

    // These fields are always present when the issue is CLOSED.
    closure_statement?: string;
    closed_timestamp?: string;
    closed_by?: string;
}

/** A request to create a new Issue. */
export interface CreateIssueRequest {
    status: IssueStatus.DRAFT_OPEN | IssueStatus.OPEN;
    owner_subject: string;
    title: string;
    due_date: string;
    description: string;
    source: string;
    priority: IssuePriority;
    remediation_plan: string;
    risk_assessment: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    files: FileToBeUploaded[];
    tags: string[];
}

/** A request to update an existing Issue. */
export interface UpdateIssueRequest {
    status: IssueStatus;
    owner_subject: string;
    title: string;
    due_date: string;
    description: string;
    priority: IssuePriority;
    source: string;
    remediation_plan: string;
    risk_assessment: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    file_updates: FileUpdates;
    tags: string[];

    closure_statement?: string;
}

/**
 * A DTO used to make Issue presentable to front-end clients. In short, the only differences between an instance of this class and the `Issue` it's derived from are:
 * 1. It provides "full" details for the Controls impacted by the Issue, rather than just the Control IDs.
 * 2. It identifies itself as belonging to the Operational Controls module.
 */
export type ControlIssueResponse = Omit<Issue, 'impacted_controls'> & { type: IssuesExceptionsModule.CONTROLS; impacted_controls: OperationalControl[] };

/**
 * A DTO used to make Issue presentable to front-end clients. In short, the only difference between an instance of this class and the `Issue` it's derived from is:
 * 1. It identifies itself as belonging to the TPRM module.
 */
export type ThirdPartyIssueResponse = Issue & { type: IssuesExceptionsModule.TPRM };

/** A DTO used to make Issue presentable to front-end clients. */
export type IssueResponse = ControlIssueResponse | ThirdPartyIssueResponse;

/** This object defines changes to an Issue's attributes that are necessary when the Issue is saved as a history record. */
export type IssueHistoryModifications = Omit<Issue, 'tags'> & { tags: DisplayableTag[] };

/**
 * A DTO used to make an IssueHistoryModifications presentable to front-end clients. In short, the only differences between an instance of this class and the `IssueHistoryModifications` it's derived from are:
 * 1. It provides "full" details for the Controls impacted by the IssueHistoryModifications, rather than just the Control IDs.
 * 2. It identifies itself as belonging to the Operational Controls module.
 */
export type ControlIssueHistoryResponse = Omit<IssueHistoryModifications, 'impacted_controls'> & { type: IssuesExceptionsModule.CONTROLS; impacted_controls: OperationalControl[] };

/**
 * A DTO used to make an IssueHistoryModifications presentable to front-end clients. In short, the only difference between an instance of this class and the `IssueHistoryModifications` it's derived from is:
 * 1. It identifies itself as belonging to the TPRM module.
 */
export type ThirdPartyIssueHistoryResponse = IssueHistoryModifications & { type: IssuesExceptionsModule.TPRM };

/** A DTO used to make IssueHistory presentable to front-end clients. */
export interface IssueHistoryResponse {
    timestamp: string;
    issue: ControlIssueHistoryResponse | ThirdPartyIssueHistoryResponse;
}

/**
 * A DTO used to make an Issue presentable to front-end clients for the Export feature.
 * The difference is the Export version of an Issue needs UI-friendly versions of Controls or Third-Party names, depending on which feature the Issue belongs to.
 */
export interface IssueExportResponse extends Omit<Issue, 'impacted_controls' | 'impacted_vendor'> {
    impacted_controls: OperationalControl[];
    impacted_vendor?: string; // Technically this is the same data type as defined in the Issue interface, but it's listed here in order to highlight that this is the Third-Party's name instead of their UUID.
}

export enum IssuesSortFilterOptions {
    STATUS = 'status',
    OWNER = 'owner_subject',
    TITLE = 'title',
    DUE_DATE = 'due_date',
    DESCRIPTION = 'description',
    PRIORITY = 'priority',
    SOURCE = 'source',
    CONTROLS = 'controls',
    THIRD_PARTY = 'impacted_vendor',
    TAGS = 'tags',
}

export enum IssuePriority {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

const priorityNumberValue = (priority: IssuePriority) => {
    switch (priority) {
        case IssuePriority.LOW:
            return 0;
        case IssuePriority.MEDIUM:
            return 1;
        case IssuePriority.HIGH:
            return 2;
    }
};

export const priorityComparator = (priorityA: IssuePriority, priorityB: IssuePriority): number => {
    if (priorityA === priorityB) {
        return 0;
    } else {
        const valueA = priorityNumberValue(priorityA);
        const valueB = priorityNumberValue(priorityB);
        return valueA < valueB ? 1 : -1;
    }
};

export const IssuePriorityOptions: OptionType[] = [
    {
        value: IssuePriority.LOW,
        label: toTitleCase(IssuePriority.LOW),
    },
    {
        value: IssuePriority.MEDIUM,
        label: toTitleCase(IssuePriority.MEDIUM),
    },
    {
        value: IssuePriority.HIGH,
        label: toTitleCase(IssuePriority.HIGH),
    },
];

export const IssuePriorityFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: IssuesSortFilterOptions.PRIORITY,
        label: 'Low',
        value: IssuePriority.LOW,
    },
    {
        groupId: IssuesSortFilterOptions.PRIORITY,
        label: 'Medium',
        value: IssuePriority.MEDIUM,
    },
    {
        groupId: IssuesSortFilterOptions.PRIORITY,
        label: 'High',
        value: IssuePriority.HIGH,
    },
];

export enum IssuePriorityFilter {
    ALL = 'ALL',
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

export const isValidIssuePriorityFilter = (value: unknown): value is IssuePriorityFilter => typeof value === 'string' && Object.values(IssuePriorityFilter).includes(value as IssuePriorityFilter);

export const IssuePriorityFilterOptions: OptionType[] = [
    {
        value: IssuePriorityFilter.ALL,
        label: 'All Priorities',
    },
    {
        value: IssuePriorityFilter.LOW,
        label: toTitleCase(IssuePriorityFilter.LOW),
    },
    {
        value: IssuePriorityFilter.MEDIUM,
        label: toTitleCase(IssuePriorityFilter.MEDIUM),
    },
    {
        value: IssuePriorityFilter.HIGH,
        label: toTitleCase(IssuePriorityFilter.HIGH),
    },
];

export enum IssueStatus {
    DRAFT_OPEN = 'DRAFT_OPEN',
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
}

export const IssueStatusFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: IssuesSortFilterOptions.STATUS,
        label: 'Open',
        value: IssueStatus.OPEN,
    },
    {
        groupId: IssuesSortFilterOptions.STATUS,
        label: 'Draft Open',
        value: IssueStatus.DRAFT_OPEN,
    },
    {
        groupId: IssuesSortFilterOptions.STATUS,
        label: 'Closed',
        value: IssueStatus.CLOSED,
    },
];

export const statusComparator = (statusA: IssueStatus, statusB: IssueStatus): number => {
    const statusValue = (status: IssueStatus): number => {
        switch (status) {
            case IssueStatus.DRAFT_OPEN:
                return 0;
            case IssueStatus.OPEN:
                return 1;
            case IssueStatus.CLOSED:
                return 2;
        }
    };

    const statusAValue = statusValue(statusA);
    const statusBValue = statusValue(statusB);
    return statusAValue - statusBValue;
};

export const sortIssues = (issues: IssueResponse[], users: UserResponse[], sortBy: IssuesSortFilterOptions, sortDirection: SortDirection, thirdPartyIdToNameMap: Map<string, string> = new Map([])): void => {
    issues.sort((issueA, issueB) => {
        let sortResult = 0;

        switch (sortBy) {
            case IssuesSortFilterOptions.STATUS:
                sortResult = statusComparator(issueA.status, issueB.status);
                break;
            case IssuesSortFilterOptions.OWNER:
                sortResult = compareUsersBySubjectForSorting(issueA.owner_subject, issueB.owner_subject, users);
                break;
            case IssuesSortFilterOptions.DUE_DATE:
                sortResult = issueA.due_date < issueB.due_date ? -1 : 1;
                break;
            case IssuesSortFilterOptions.PRIORITY:
                sortResult = priorityComparator(issueA.priority, issueB.priority);
                break;
            case IssuesSortFilterOptions.CONTROLS:
                if (issueA.type === IssuesExceptionsModule.CONTROLS && issueB.type === IssuesExceptionsModule.CONTROLS) {
                    sortResult = issueA.impacted_controls.length - issueB.impacted_controls.length;
                } else {
                    throw new Error('Cannot sort TPRM issues by control.');
                }
                break;
            case IssuesSortFilterOptions.THIRD_PARTY:
                if (issueA.type === IssuesExceptionsModule.TPRM && issueB.type === IssuesExceptionsModule.TPRM) {
                    sortResult = thirdPartyNameComparator(issueA.impacted_vendor!, issueB.impacted_vendor!, thirdPartyIdToNameMap);
                } else {
                    throw new Error('Cannot sort Operational Controls issues by third party.');
                }
                break;
            // Can't really sort by Tags.
            case IssuesSortFilterOptions.TAGS:
                sortResult = 0;
                break;
            default:
                sortResult = issueA[sortBy].localeCompare(issueB[sortBy]);
                break;
        }

        return sortDirection === SortDirection.ASC ? sortResult : -sortResult;
    });
};

/**
 * This relatively simple function exists because we have previously had inconsistencies in whether drafts are considered "open".
 * @returns The number of issues in the provided list that are considered "open".
 */
export const countOpenIssues = (issues: IssueResponse[]): number => issues.filter((issue) => issue.status === IssueStatus.OPEN).length;
